import { useT } from "../../../shared/i18n/i18n";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Boxes, CheckCircle2, MoreHorizontal } from "lucide-react";
import type { WorkObjectTypeDefinition } from "../model/use-work-object-types";
import { WorkObjectTypeIconGlyph } from "./work-object-type-icon";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";

type WorkObjectTypeListProps = {
  onSelect: (type: WorkObjectTypeDefinition) => void;
  types: WorkObjectTypeDefinition[];
};

export function WorkObjectTypeList({ onSelect, types }: WorkObjectTypeListProps) {
  const t = useT();
  const columns = useMemo<ColumnDef<WorkObjectTypeDefinition>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        enableHiding: false,
        enableSorting: true,
        header: t("table.name"),
        minSize: 180,
        size: 240,
        meta: {
          locked: true,
        },
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-2">
            <WorkObjectTypeIconGlyph className="shrink-0 text-muted-foreground" icon={row.original.icon} />
            <span className="min-w-0 truncate">{row.original.name}</span>
          </div>
        ),
      },
      {
        id: "category",
        accessorFn: (type) => type.category?.name ?? "No category",
        enableSorting: true,
        header: t("table.category"),
        minSize: 140,
        size: 160,
        cell: ({ row }) => <span className="truncate text-muted-foreground">{row.original.category?.name ?? "No category"}</span>,
      },
      {
        id: "key",
        accessorKey: "key",
        enableSorting: true,
        header: t("table.key"),
        minSize: 140,
        size: 180,
        cell: ({ row }) => <span className="truncate text-muted-foreground">{row.original.key}</span>,
      },
      {
        id: "actions",
        enableHiding: false,
        enableResizing: false,
        enableSorting: false,
        header: "",
        maxSize: 36,
        minSize: 36,
        size: 36,
        meta: {
          cellClassName: "justify-end px-1",
          headerClassName: "justify-end px-1",
          locked: true,
        },
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button aria-label={t("a11y.typeActions", { name: row.original.name })} className="size-6" size="icon-xs" variant="ghost">
                    {row.original.canDelete ? <MoreHorizontal /> : <CheckCircle2 />}
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="w-32">
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => onSelect(row.original)}>{t("itemTypes.configure")}</DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [onSelect],
  );

  if (types.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Boxes />
          </EmptyMedia>
          <EmptyTitle>{t("itemTypes.noActiveTypes")}</EmptyTitle>
          <EmptyDescription>{t("itemTypes.createFirst")}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <DataTable
      columns={columns}
      columnOptionsHostColumnId="actions"
      data={types}
      defaultColumnOrder={["name", "category", "key", "actions"]}
      enableColumnVisibility
      fillColumnId="key"
      fixedColumnIds={["name", "actions"]}
      getRowId={(type) => type.id}
      onRowClick={(row) => onSelect(row.original)}
      stickyColumnIds={["name", "actions"]}
      tableClassName="min-w-[620px]"
    />
  );
}
